import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { tmOAuthConfig } from '../models/tm-oauth-config';
import { ApiService } from '../../shared/services/core/api.service';
import { ProviderName } from '../../account/models/login-response.model';
import { SocialLink } from '../models/socialLink.model';
import { map, catchError } from 'rxjs/operators';
import { MemberQuery } from '../../account/member/member.query';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TMOauthService {
  constructor(
    private api: ApiService,
    private http: HttpClient,
    private cookieService: CookieService,
    private oauthConfig: tmOAuthConfig,
    private memberQuery: MemberQuery
  ) {
    this.oauthConfig = Object.assign({} as tmOAuthConfig, new tmOAuthConfig(), oauthConfig);
  }

  /**
   * Use this once the customer has gone from TM back to Fortress to complete the OAuth
   * @param code this is the code that is returned in the url
   */
  callBackToken(code: string) {
    let params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', this.oauthConfig.clientId!);
    params.append('client_secret', this.oauthConfig.clientSecret!);
    params.append('code', code);

    let headers = new HttpHeaders({
      'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
    });

    this.http.post(this.oauthConfig.tokenUri!, params.toString(), { headers: headers }).subscribe(
      (data) => this._saveToken(data),
      (err) => {
        alert('Unable to login to Ticket Master');
      }
    );
  }

  /**
   * will return a tm connections from the api
   */
  private _getToken(): Observable<SocialLink> {
    return this.api.getAnySingle<SocialLink>('social/' + ProviderName.TicketMaster);
  }

  /**
   * sets the token, if the useCookie is turned on the will set it to the api
   *
   * @param token full token from the response of tm
   */
  private _setToken(token: any) {
    let access_token = token.access_token;
    let refresh_token = token.refresh_token;

    if (this.oauthConfig.useCookie) {
      let expires = token.expires_in;
      let expiresDate = new Date().getTime() + 1000 * expires;

      this.cookieService.set(this.oauthConfig.amCookieName!, refresh_token, expiresDate, '/');
    } else {
      let memberContext = this.memberQuery.getUserContext();
      let connection = {
        AccessToken: access_token,
        LoginSocialId: 0,
        ProviderId: ProviderName.TicketMaster,
        SocialMembershipId: '',
        Key1: refresh_token,
        Key2: '',
        Username: memberContext.portalId,
        PortalId: memberContext.portalId,
        ClubId: memberContext.clubId,
      } as SocialLink;
      this.api.post<SocialLink>('social', connection).toPromise();
    }
  }

  private _saveToken(token: any) {
    let access_token = token.access_token;
    this._setToken(token);
    window.location.href = this.oauthConfig.loginUri + '?accessToken=' + access_token + '&redir=myevents';
  }

  loginUsingCurrentToken(refreshToken: string) {
    let params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('client_id', this.oauthConfig.clientId!);
    params.append('client_secret', this.oauthConfig.clientSecret!);
    params.append('refresh_token', refreshToken);

    let headers = new HttpHeaders({
      'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
    });

    this.http.post(this.oauthConfig.tokenUri!, params.toString(), { headers: headers }).subscribe(
      (data: any) => {
        window.location.href = this.oauthConfig.loginUri + '?accessToken=' + data.access_token + '&redir=myevents';
      },
      (err: any) => {
        this.cookieService.delete(this.oauthConfig.amCookieName!, '/');
        alert('Unable to login to Ticket Master');
      }
    );
  }

  /**
   * This is the main starting point of the oauth integration with TM
   */
  authenticate() {
    let redirectUrl =
      this.oauthConfig.authoriseUri +
      '?client_id=' +
      this.oauthConfig.clientId +
      '&redirect_uri=' +
      this.oauthConfig.callBackUri +
      '&response_type=code';
    if (this.oauthConfig.useCookie) {
      var loginToken = this.cookieService.get(this.oauthConfig.amCookieName!);
      if (loginToken != null && loginToken != '') {
        this.loginUsingCurrentToken(loginToken);
      } else {
        window.location.href = redirectUrl;
      }
    } else {
      this._getToken()
        .pipe(
          map((x) => {
            if (x != null) {
              this.loginUsingCurrentToken(x.Key1);
            } else {
              window.location.href = redirectUrl;
            }
          }),
          catchError((err) => {
            window.location.href = redirectUrl;
            return of([]);
          })
        )
        .toPromise();
    }
  }
}
